// The YOUR BIDS manager (bids page): renders the connected wallet's bids —
// each owning its coins, filling `count` times through independent slots,
// made of one or more components (collection / trait / list targets at their
// own prices) — keeps all edits as an in-memory draft, and realizes them in
// ONE confirm — [coin-management tx] → [one funding batch] → [statement
// signature] — via flows.applyBidChanges. Nothing here mutates anything
// before CONFIRM ALL CHANGES.
//
// New components ("targets" in the UI) are OPEN rows edited in place
// (+ TARGET just opens another row — no per-row commit); CONFIRM blocks
// while any row is incomplete, and a bid auto-removes when its last row is
// removed. A PLACED bid is locked: it cancels whole, never per target.
import { api, bidAllocation, bidDraft, bidStorage, btc, flows, placement } from "./trade.js";
import { currentAccount } from "./connect.js";
import { toast } from "./ui.js";
import { btcShort } from "./collection-bid.js";
import { recordChange, type PlacedBid } from "./local-changes.js";
import { priceGrid, type PriceGroup } from "./price-grid.js";
import type { AttributeFilter, AttributeOptions, Bid, BidKind } from "../client/api.js";
import type {
  BidDraft,
  ClosedComponent,
  DraftState,
  OpenComponent,
  ResolveContext,
  SlotDraft,
} from "../client/bid-draft.js";
import { t } from "../client/i18n.js";

type TargetsDoc = Awaited<ReturnType<typeof api.collectionApi.targets>>;

const spriteCol = (id: number) => (id - 1) % 100;
const spriteRow = (id: number) => Math.floor((id - 1) / 100);

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

// Status / kind names shown to the user (keys stay English in the data).
const STATUS_LABEL: Record<string, () => string> = {
  new: () => t("client.manager.status_new"),
  removed: () => t("client.manager.status_removed"),
  changed: () => t("client.manager.status_changed"),
};
const KIND_LABEL: Record<string, () => string> = {
  collection: () => t("client.manager.kind_collection"),
  trait: () => t("client.manager.kind_trait"),
  list: () => t("client.manager.kind_list"),
  custom: () => t("client.manager.kind_custom"),
};
const kindLabel = (kind: string) => KIND_LABEL[kind]?.() ?? kind;

const badge = (status: string): HTMLElement | null => {
  if (status === "kept" || status === "open") return null;
  return el("span", `diff-badge ${status}`, STATUS_LABEL[status]?.() ?? status);
};

/** The listed monkes' art for a list component (links to each monke). */
const spriteStrip = (ids: number[]): HTMLElement => {
  const strip = el("div", "cond-sprites");
  for (const id of ids) {
    const link = document.createElement("a");
    link.href = `/${id}`;
    link.title = `#${id}`;
    const sprite = el("span", "msprite s28");
    sprite.style.setProperty("--col", String(spriteCol(id)));
    sprite.style.setProperty("--row", String(spriteRow(id)));
    sprite.setAttribute("role", "img");
    sprite.setAttribute("aria-label", `Monke #${id}`);
    link.appendChild(sprite);
    strip.appendChild(link);
  }
  return strip;
};

/** Live preview parse of the list textarea: every well-formed in-range
 * number as typed (duplicates once), no validation — that stays with
 * parseMonkeList at confirm. */
const previewMonkes = (text: string): number[] => {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const line of text.split("\n")) {
    const m = /^\s*#?(\d+)\s*$/.exec(line);
    if (!m) continue;
    const id = Number(m[1]);
    if (id < 1 || id > 10000 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

const scopeLabel = (f: AttributeFilter | null): string => {
  const monkes = (f as { monkes?: number[] } | null)?.monkes;
  if (Array.isArray(monkes)) return monkes.map((n) => `#${n}`).join(", ");
  const entries = Object.entries(f ?? {}).filter(([, v]) => v != null);
  if (entries.length === 0) return t("client.manager.any_monke");
  return entries.map(([k, v]) => `${k}: ${v}`).join(", ");
};

/** Per-fill prices as one text: "0.05" or "0.05 / 0.045 / 0.04". */
const pricesText = (bid: BidDraft, c: ClosedComponent): string =>
  bid.pricing === "ladder" ? c.prices.map(btcShort).join(" / ") : btcShort(c.prices[0] ?? 0);

export function initBidManager(opts: {
  isPageBusy: () => boolean;
  insertMarketRows: (bid: PlacedBid) => void;
  removeMarketBid: (bidId: string) => void;
  removeMarketComponent: (bidId: string, componentIndex: number) => void;
}): { reload: () => Promise<void>; confirmInFlight: () => boolean } {
  const section = document.getElementById("your-bids");
  const listHost = document.getElementById("your-bids-list");
  const bar = document.getElementById("bid-confirm-bar");
  const account = currentAccount();
  if (!section || !listHost || !bar) {
    return { reload: async () => {}, confirmInFlight: () => false };
  }
  const list: HTMLElement = listHost;
  const confirmBar: HTMLElement = bar;
  if (!account) {
    // Walletless: the section is CSS-hidden; nothing to do.
    return { reload: async () => {}, confirmInFlight: () => false };
  }

  const placeBtn = section.querySelector<HTMLButtonElement>("[data-place-bid]")!;
  // Re-signs every offer whose monkes moved since signing; shown only while
  // nothing is being edited (a confirm refreshes them itself).
  const refreshBtn = section.querySelector<HTMLButtonElement>("[data-refresh-bids]")!;
  const retryBtn = section.querySelector<HTMLButtonElement>("[data-retry]")!;
  // Creates that failed AFTER the statement/broadcast went through: their
  // signed fundings are held by the flow, so RETRY re-submits them with no
  // new wallet prompt (the only way a confirm can end half-applied).
  let pendingRetry: (() => Promise<flows.BidChangeResult>) | null = null;
  const confirmBtn = confirmBar.querySelector<HTMLButtonElement>("[data-confirm-all]")!;
  const discardBtn = confirmBar.querySelector<HTMLButtonElement>("[data-discard]")!;
  const utxoNote = confirmBar.querySelector<HTMLElement>("[data-utxo-note]")!;
  const allocError = confirmBar.querySelector<HTMLElement>("[data-alloc-error]")!;
  const progress = confirmBar.querySelector<HTMLElement>("[data-confirm-progress]")!;
  section.classList.add("busy-host");

  let state: DraftState | null = null;
  let serverBids: Bid[] = [];
  let doc: TargetsDoc | null = null;
  let attributes: AttributeOptions | null = null;
  let free: { outpoint: string; valueSats: number }[] = [];
  let live: ReadonlySet<string> = new Set();
  let plan: ReturnType<typeof bidAllocation.planAllocation> | null = null;
  // The market data the PLAN needs (targets doc, attributes, free coins)
  // loads in the background; bids render as soon as they arrive.
  let ready: Promise<void> = Promise.resolve();
  let busy = false;
  // Per-bid funds line, updated on keystrokes without a DOM rebuild.
  const fundsLines = new Map<string, HTMLElement>();
  // Touched-field validation: a control shows its red border only once it
  // has been blurred (or a confirm was attempted), and the summary error
  // line appears only while at least one control is red.
  const touched = new Set<string>();
  let confirmAttempted = false;
  type OpenControls = {
    group?: PriceGroup; // set by whichever line hosts the row's price group
    list?: HTMLTextAreaElement;
    traits: Map<string, { key: HTMLSelectElement; value: HTMLSelectElement }>;
  };
  const openControls = new Map<string, OpenControls>();
  const countControls = new Map<string, HTMLInputElement>();

  const unloadGuard = (e: BeforeUnloadEvent) => e.preventDefault();
  const setGuard = (on: boolean) => {
    window.removeEventListener("beforeunload", unloadGuard);
    if (on) window.addEventListener("beforeunload", unloadGuard);
  };

  const busyOverlay = el("div", "modal-busy");
  busyOverlay.hidden = true;
  busyOverlay.appendChild(el("div", "spinner"));
  section.appendChild(busyOverlay);
  const setBusy = (on: boolean) => {
    busy = on;
    busyOverlay.hidden = !on;
    section.classList.toggle("is-busy", on);
    progress.hidden = !on;
    if (!on) progress.textContent = "";
  };

  const resolveCtx = (): ResolveContext => ({
    targetsByMonkeId: new Map(
      (doc?.items ?? [])
        .filter((t): t is typeof t & { monkeId: number } => t.monkeId != null)
        .map((t) => [t.monkeId, { inscriptionId: t.inscriptionId, owner: t.owner }]),
    ),
    selfAddress: account.ordinalsAddress,
  });

  function applyBids(bids: Bid[]): void {
    serverBids = bids;
    state = bidDraft.fromServer(bids, account!.ordinalsAddress);
    touched.clear();
    confirmAttempted = false;
    render();
  }

  /** Bids paint as soon as the (small) bids call returns; the targets
   * document (megabytes), trait options and the wallet's free coins only
   * feed the allocation plan, so they load behind it and re-plan on
   * arrival — confirm awaits them. */
  async function load(): Promise<void> {
    const bidder = account!.ordinalsAddress;
    const bidsP = api.bidsApi.list(bidder);
    const auxP = Promise.all([
      api.collectionApi.targets(),
      api.collectionApi.attributes().catch(() => null),
      bidsP.then((b) => flows.walletCoins(account!, b)),
    ]);
    ready = auxP.then(([targets, attrs, coins]) => {
      doc = targets;
      attributes = attrs;
      free = coins.free.map((u) => ({ outpoint: `${u.txid}:${u.vout}`, valueSats: u.value }));
      live = coins.live;
      refreshSummary();
    });
    ready.catch(() => {}); // surfaced where awaited
    const bids = await bidsP;
    applyBids(bids);
    list.classList.remove("is-stale");
    // The parse-time reserve has done its job — drop it BEFORE measuring:
    // min-height pins the box, so a height measured under it could only
    // ever grow, and cancelled bids would leave empty space for good.
    document.documentElement.style.removeProperty("--your-bids-h");
    bidStorage.saveBidsSnapshot(bidder, bids);
    bidStorage.saveListLayout(bidder, window.innerWidth, list.getBoundingClientRect().height);
  }

  /** Does a persisted slot's set still cover what its bid now asks of it
   * (the allocator's own reuse rule)? */
  function covers(bid: BidDraft, slot: SlotDraft): boolean {
    if (!doc) return true;
    const reserved = slot.utxos.reduce((a, u) => a + u.valueSats, 0);
    return (
      reserved >=
      bidDraft.requiredSats(bid, slot.slotIndex, doc.itemFeeSats) +
        bidAllocation.fundFeeAt(slot.utxos.length, btc.paymentInputVb(account!.paymentAddressType), doc.fundingFeeRate)
    );
  }

  /** A persisted bid whose reserved coins the wallet no longer holds in
   * full: untouchable for planning (never re-carved or recycled) and, when
   * edited, recreated on fresh coins — exactly like a legacy bid. */
  const stale = (bid: BidDraft): boolean => bid.slots.some((slot) => placement.staleSlot(slot, live));

  /** A persisted bid an edit outgrew (or a legacy/stale one being edited)
   * is RECREATED on new coins: every slot re-funded, the old ones freed. */
  const recreates = (bid: BidDraft): boolean =>
    bid.serverId !== null &&
    bid.status !== "removed" &&
    bid.components.some((c) => c.status !== "kept") &&
    (bid.legacy || stale(bid) || bid.slots.some((slot) => !covers(bid, slot)));

  function replan(): void {
    if (!state || !doc) return;
    const itemFee = doc.itemFeeSats;
    plan = bidAllocation.planAllocation({
      freeUtxos: free,
      // Coins freed by bids REMOVED or recreated in this draft (cancelled
      // before any create) are usable by the new fundings — no
      // coin-management tx just to re-shape money the confirm itself
      // releases.
      freedUtxos: state.bids
        .filter((b) => b.serverId && !b.legacy && !stale(b) && (b.status === "removed" || recreates(b)))
        .flatMap((b) => b.slots.flatMap((slot) => slot.utxos)),
      groups: state.bids
        .filter((b) => b.status !== "removed")
        .flatMap((b) => {
          const dirty = b.status === "new" || b.components.some((c) => c.status !== "kept");
          const fresh = b.serverId === null || recreates(b);
          return b.slots.map((slot) => ({
            key: slot.key,
            existingUtxos: fresh ? null : slot.utxos,
            requiredSats: bidDraft.requiredSats(b, slot.slotIndex, itemFee),
            dirty,
            legacy: (b.legacy || stale(b)) && !fresh,
          }));
        }),
      fundingFeeRate: doc.fundingFeeRate,
      // The mgmt tx broadcasts NOW — pay the live standard tier.
      mgmtFeeRate: readStandardFee(),
      inputVb: btc.paymentInputVb(account!.paymentAddressType),
    });
  }

  function readStandardFee(): number {
    try {
      const raw = document.documentElement.dataset.fees;
      if (raw) return Math.max(1, (JSON.parse(raw) as { halfHourFee: number }).halfHourFee);
    } catch {
      /* fall through */
    }
    return 1;
  }

  /** Recompute plan + confirm-bar + per-bid funds lines + red borders
   * WITHOUT rebuilding the list DOM — safe on every keystroke. Red borders
   * appear only on touched (blurred) controls or after a confirm attempt,
   * and the "finish your bids" line only while some control is red. */
  function refreshSummary(): void {
    if (!state) return;
    replan();
    const dirty = bidDraft.isDirty(state);
    setGuard(dirty);
    confirmBar.hidden = !dirty;
    const stale = staleOfferIds();
    refreshBtn.hidden = dirty || stale.length === 0;
    if (!busy) refreshBtn.textContent = t("client.manager.refresh_bids", { n: stale.length });
    if (!busy) progress.textContent = doc ? "" : t("client.manager.loading_market");
    for (const bid of state.bids) {
      const line = fundsLines.get(bid.key);
      if (line) line.textContent = fundsText(bid);
    }

    // Field validation → borders. A control goes red iff invalid AND
    // (touched OR a confirm attempt happened); the first red error becomes
    // the summary text.
    const ctx = resolveCtx();
    const redErrors: string[] = [];
    const mark = (control: HTMLElement | undefined, touchKey: string, error?: string) => {
      if (!control) return;
      const red = !!error && (confirmAttempted || touched.has(touchKey));
      control.classList.toggle("field-invalid", red);
      if (red && error) redErrors.push(error);
    };
    for (const bid of state.bids) {
      if (bid.status === "removed") continue;
      mark(countControls.get(bid.key), `${bid.key}:count`, bidDraft.bidFieldErrors(bid).count);
      for (const c of bid.components) {
        if (c.status !== "open") continue;
        const controls = openControls.get(c.key);
        if (!controls) continue;
        const fieldErrors = bidDraft.openComponentFieldErrors(c, bid, ctx);
        mark(controls.group?.priceInput, `${c.key}:price`, fieldErrors.price);
        mark(controls.group?.stepInput, `${c.key}:step`, fieldErrors.step);
        controls.group?.setCopySource(bidDraft.priceAbove(state, bid.key, c.key));
        mark(controls.list, `${c.key}:list`, fieldErrors.list);
        for (const [pairId, pair] of controls.traits) {
          const keyError = fieldErrors.traits?.find((t) => t.pairId === pairId && t.field === "key")?.error;
          const valueError = fieldErrors.traits?.find((t) => t.pairId === pairId && t.field === "value")?.error;
          mark(pair.key, `${c.key}:${pairId}:key`, keyError);
          mark(pair.value, `${c.key}:${pairId}:value`, valueError);
        }
      }
    }

    if (!dirty || !plan) return;
    // Bid-level problems (shape, duplicates) have no single field to
    // redden — they show whenever present. CONFIRM itself only disables
    // for funds.
    const bidErrors = bidDraft.draftErrors(state, ctx).filter((e) => e.compKey === null);
    confirmBtn.disabled = plan.shortfallSats > 0;
    confirmBtn.title = plan.shortfallSats > 0 ? t("client.manager.not_enough_funds_title") : "";
    utxoNote.hidden = plan.mgmt === null;
    if (plan.mgmt) {
      // Bids the ALLOCATOR pulled into the re-fund (their reservation's
      // excess is needed elsewhere) as opposed to ones the user edited.
      const userDirty = new Set(
        state.bids
          .filter((b) => b.status !== "kept" || b.components.some((c) => c.status !== "kept"))
          .flatMap((b) => b.slots.map((slot) => slot.key)),
      );
      const recarved = new Set(
        plan.refund.filter((key) => !userDirty.has(key)).map((key) => key.split("#")[0]),
      ).size;
      utxoNote.textContent =
        t("client.manager.utxo_note", { fee: plan.mgmt.feeSats.toLocaleString() }) +
        (recarved > 0 ? t("client.manager.utxo_note_recarved", { n: recarved }) : "");
    }
    const message =
      redErrors.length > 0
        ? t("client.manager.finish_bids", { error: redErrors[0]! })
        : bidErrors.length > 0
          ? bidErrors[0]!.error
          : plan.shortfallSats > 0
            ? t("client.manager.insufficient", { amount: btcShort(plan.shortfallSats) })
            : null;
    allocError.hidden = message === null;
    if (message !== null) allocError.textContent = message;
  }

  function fundsText(bid: BidDraft): string {
    const itemFee = doc?.itemFeeSats ?? 0;
    const need = bid.slots.reduce((a, slot) => a + bidDraft.requiredSats(bid, slot.slotIndex, itemFee), 0);
    const n = bid.slots.length;
    // A bid the executable plan re-funds — its own edit outgrew a set, or
    // the preparation step is re-carving its reservation for other bids —
    // has no stable reserved amount until confirm: show the requirement
    // only, like a new bid. (On a shortfall the plan isn't happening, so
    // the real current reservation stays shown.)
    const refunding =
      plan !== null && plan.shortfallSats === 0 && bid.slots.some((slot) => plan!.refund.includes(slot.key));
    const times = n > 1 ? `×${n}` : ""; // fills left, only when more than one
    if (!bid.serverId || refunding || recreates(bid)) {
      return `${t("client.manager.needs", { amount: btcShort(need) })}${times ? ` ${times}` : ""}`;
    }
    // A placed bid: no coin amounts (the reserved coin is often far bigger
    // than the bid and only confuses) — just the fills left and, for a
    // custom bid, the priciest target per fill.
    const live = bid.components.filter((c): c is ClosedComponent => c.status !== "open" && c.status !== "removed");
    const perFill = Math.max(0, ...live.map((c) => Math.max(...c.prices)));
    return [times, live.length > 1 ? t("client.manager.up_to_per_fill", { amount: btcShort(perFill) }) : ""].filter(Boolean).join(" · ");
  }

  function render(): void {
    if (!state || !list) return;
    fundsLines.clear();
    openControls.clear();
    countControls.clear();
    list.replaceChildren();
    const bids = state.bids;
    if (bids.length === 0) {
      list.appendChild(el("p", "dim", t("client.manager.no_bids")));
    }
    for (const bid of bids) list.appendChild(renderBid(bid));
    refreshSummary();
  }

  const KINDS: BidKind[] = ["collection", "trait", "list", "custom"];

  function renderBid(bid: BidDraft): HTMLElement {
    const card = el("div", "bid-card");
    card.dataset.bidKey = bid.key;
    const head = el("div", "bid-card-head");
    head.appendChild(el("span", "diff-badge kind", kindLabel(bid.kind)));
    const live = bid.components.filter((c) => c.status !== "removed");
    const first = live[0];
    const title =
      bid.kind === "custom"
        ? t("client.manager.targets", { n: live.length })
        : first && first.status !== "open"
          ? scopeLabel(first.attributeFilter)
          : t("client.manager.new_bid");
    head.appendChild(el("strong", undefined, title));
    if (bid.legacy) head.appendChild(el("span", "diff-badge", t("client.manager.legacy")));
    const b = badge(bid.status);
    if (b) head.appendChild(b);
    const funds = el("span", "dim", fundsText(bid));
    fundsLines.set(bid.key, funds);
    head.appendChild(funds);
    head.appendChild(el("span", "spacer"));
    if (bid.status === "removed") {
      const undo = el("button", "btn btn-ghost", t("client.manager.undo"));
      undo.type = "button";
      undo.onclick = () => {
        if (busy) return;
        state = bidDraft.restoreBid(state!, bid.key);
        render();
      };
      head.appendChild(undo);
    } else {
      const rm = el("button", "btn btn-ghost cond-x", "×");
      rm.type = "button";
      rm.title = t("client.manager.cancel_this_bid");
      rm.onclick = () => {
        if (busy) return;
        state = bidDraft.removeBid(state!, bid.key);
        render();
      };
      head.appendChild(rm);
    }
    card.appendChild(head);

    if (bid.serverId === null) card.appendChild(renderShapeLine(bid));

    for (const c of bid.components) {
      if (c.status === "removed" && !c.offerIds.some((id) => id !== null)) continue;
      const node = c.status === "open" ? renderOpenRow(bid, c) : renderClosedRow(bid, c);
      // A simple bid's open collection row has nothing of its own to show
      // (its price sits on the shape line).
      if (node.childElementCount === 0) continue;
      node.classList.add("cond-row");
      node.dataset.compKey = c.key;
      card.appendChild(node);
    }

    // Only a NEW custom bid takes more targets (a placed bid is locked).
    if (bid.kind === "custom" && bid.serverId === null && bid.status !== "removed" && live.length < bidDraft.MAX_COMPONENTS) {
      const add = el("button", "btn btn-ghost", t("client.manager.add_target"));
      add.type = "button";
      add.style.marginTop = "6px";
      add.onclick = () => {
        if (busy) return;
        state = bidDraft.addComponent(state!, bid.key);
        render();
      };
      card.appendChild(add);
    }
    return card;
  }

  const field = (labelText: string, control: HTMLElement) => {
    const label = el("label", "field");
    label.appendChild(el("span", undefined, labelText));
    label.appendChild(control);
    return label;
  };

  /** A NEW bid's shape: type · count, then the pricing — the select alone
   * for a custom bid (its rows price themselves), the whole price group for
   * a simple bid (wrapping to a second line as one unit when full). */
  function renderShapeLine(bid: BidDraft): HTMLElement {
    const line = el("div", "bid-shape");
    const kindSelect = el("select");
    for (const k of KINDS) kindSelect.append(new Option(kindLabel(k), k));
    kindSelect.value = bid.kind;
    kindSelect.onchange = () => {
      state = bidDraft.updateBid(state!, bid.key, { kind: kindSelect.value as BidKind });
      render();
    };
    line.appendChild(field(t("client.manager.bid_type"), kindSelect));

    const countInput = el("input");
    countInput.inputMode = "numeric";
    countInput.style.width = "4em";
    countInput.value = bid.countText;
    countInput.title = t("client.manager.count_title", { max: bidDraft.MAX_COUNT });
    countInput.onchange = () => {
      touched.add(`${bid.key}:count`);
      state = bidDraft.updateBid(state!, bid.key, { countText: countInput.value });
      render();
    };
    countControls.set(bid.key, countInput);
    line.appendChild(field(t("client.manager.count"), countInput));

    if (bid.kind === "custom") {
      const pricingSelect = el("select");
      pricingSelect.append(
        new Option(t("client.manager.same_price"), "same"),
        new Option(t("client.manager.ladder"), "ladder"),
      );
      pricingSelect.value = bid.pricing;
      pricingSelect.disabled = bid.slots.length < 2;
      pricingSelect.title = t("client.manager.ladder_title");
      pricingSelect.onchange = () => {
        state = bidDraft.updateBid(state!, bid.key, { pricing: pricingSelect.value as BidDraft["pricing"] });
        render();
      };
      line.appendChild(field(t("client.manager.pricing"), pricingSelect));
    } else {
      const open = bid.components.find((c): c is OpenComponent => c.status === "open");
      if (open) line.appendChild(renderPriceGroup(bid, open, true));
    }
    return line;
  }

  /** An open row's price controls ([pricing] · price · [step + fills] ·
   * [↑ same as above]); state updates on input without re-rendering. */
  function renderPriceGroup(bid: BidDraft, c: OpenComponent, withPricing: boolean): HTMLElement {
    const group = priceGrid({
      count: bid.slots.length,
      pricing: bid.pricing,
      initial: { priceText: c.priceText, stepText: c.stepText },
      withPricing,
      onInput: () => {
        const { priceText, stepText } = group.read();
        state = bidDraft.updateComponent(state!, bid.key, c.key, { priceText, stepText });
        refreshSummary();
      },
      onPricing: (pricing) => {
        state = bidDraft.updateBid(state!, bid.key, { pricing });
        render();
      },
      onBlur: (control) => {
        touched.add(`${c.key}:${control}`);
        refreshSummary();
      },
    });
    group.setCopySource(bidDraft.priceAbove(state!, bid.key, c.key));
    const controls: OpenControls = openControls.get(c.key) ?? { traits: new Map() };
    controls.group = group;
    openControls.set(c.key, controls);
    return group.host;
  }

  function renderClosedRow(bid: BidDraft, c: ClosedComponent): HTMLElement {
    const row = el("div", `bid-cond${c.status === "removed" ? " removed" : ""}`);
    // A list component shows its monkes' art inline before the label; the
    // label/price group (.cond-main) drops to its own line only when it no
    // longer fits beside the sprites.
    const monkes = (c.attributeFilter as { monkes?: number[] } | null)?.monkes;
    if (Array.isArray(monkes)) row.appendChild(spriteStrip(monkes));
    const main = el("div", "cond-main");
    row.appendChild(main);
    main.appendChild(el("span", "cond-label", scopeLabel(c.attributeFilter)));
    if (c.totalItems != null) {
      const stale = (c.validItems ?? c.totalItems) < c.totalItems;
      const counts = el("span", stale ? "dim stale" : "dim", `(${c.validItems}/${c.totalItems})`);
      if (stale) counts.title = t("client.manager.stale_title");
      main.appendChild(counts);
    }
    // Dotted leader so the eye can track each component to ITS price.
    main.appendChild(el("span", "leader"));
    main.appendChild(el("span", "num", pricesText(bid, c)));
    const cb = badge(c.status);
    if (cb) main.appendChild(cb);
    // No per-target ×: a placed bid is locked and cancels whole.
    return row;
  }

  /** An in-progress component: its inputs ARE the row. Three targets —
   * collection (whole collection), trait (filter pairs), list (newline-
   * separated monke #s). In a custom bid the row also picks its target
   * type and prices itself; a simple bid's single row follows the bid type
   * and is priced on the shape line. State updates on input without
   * re-rendering (focus survives); structural changes re-render. Blur marks
   * a control "touched" so refreshSummary can red-border it once invalid. */
  function renderOpenRow(bid: BidDraft, c: OpenComponent): HTMLElement {
    const wrap = el("div", `bid-cond bid-cond-open${bid.kind === "custom" ? "" : " simple"}`);

    const patch = (p: Parameters<typeof bidDraft.updateComponent>[3]) => {
      state = bidDraft.updateComponent(state!, bid.key, c.key, p);
    };
    const touch = (suffix: string) => {
      touched.add(`${c.key}:${suffix}`);
      refreshSummary();
    };
    const controls: OpenControls = openControls.get(c.key) ?? { traits: new Map() };
    openControls.set(c.key, controls);

    if (bid.kind === "custom") {
      const condLine = el("div", "cond-line");
      wrap.appendChild(condLine);
      const kindSelect = el("select");
      kindSelect.append(
        new Option(kindLabel("collection"), "collection"),
        new Option(kindLabel("trait"), "trait"),
        new Option(kindLabel("list"), "list"),
      );
      kindSelect.value = c.kind;
      kindSelect.onchange = () => {
        const kind = kindSelect.value as OpenComponent["kind"];
        patch({ kind });
        if (kind === "trait" && c.traits.length === 0) {
          state = bidDraft.addTraitPair(state!, bid.key, c.key);
        }
        render();
      };
      condLine.appendChild(field(t("client.manager.target"), kindSelect));
      condLine.appendChild(renderPriceGroup(bid, c, false));

      const rm = el("button", "btn btn-ghost cond-x", "×");
      rm.type = "button";
      rm.title = t("client.manager.remove_target");
      rm.onclick = () => {
        if (busy) return;
        state = bidDraft.removeComponent(state!, bid.key, c.key);
        render();
      };
      condLine.appendChild(rm);
    }

    if (c.kind === "trait") {
      // One indented line per (trait, value) pair; pairs AND together
      // (e.g. Body: Beak + Count: 2).
      for (const pair of c.traits) {
        const line = el("div", "trait-line");
        const keySelect = el("select");
        keySelect.append(new Option(t("client.manager.pick_trait"), ""));
        for (const key of Object.keys(attributes ?? {})) keySelect.append(new Option(key, key));
        keySelect.value = pair.key;
        keySelect.onchange = () => {
          touched.add(`${c.key}:${pair.id}:key`);
          patch({ traits: c.traits.map((t) => (t.id === pair.id ? { ...t, key: keySelect.value, value: "" } : t)) });
          render();
        };
        keySelect.onblur = () => touch(`${pair.id}:key`);
        line.appendChild(field(t("client.manager.trait"), keySelect));

        const valueSelect = el("select");
        valueSelect.append(new Option(t("client.manager.pick_value"), ""));
        if (pair.key && attributes) {
          const values = attributes[pair.key as keyof AttributeOptions] ?? [];
          for (const v of [...values].sort((a, b) =>
            typeof a === "number" && typeof b === "number" ? a - b : String(a) < String(b) ? -1 : 1,
          )) {
            valueSelect.append(new Option(String(v), String(v)));
          }
        }
        valueSelect.value = pair.value;
        valueSelect.onchange = () => {
          touched.add(`${c.key}:${pair.id}:value`);
          patch({ traits: c.traits.map((t) => (t.id === pair.id ? { ...t, value: valueSelect.value } : t)) });
          refreshSummary();
        };
        valueSelect.onblur = () => touch(`${pair.id}:value`);
        line.appendChild(
          field(pair.key ? t("client.manager.trait_value", { trait: pair.key.toLowerCase() }) : t("client.manager.value"), valueSelect),
        );
        controls.traits.set(pair.id, { key: keySelect, value: valueSelect });

        const dropPair = el("button", "btn btn-ghost", "×");
        dropPair.type = "button";
        dropPair.title = t("client.manager.remove_trait");
        dropPair.onclick = () => {
          if (busy) return;
          patch({ traits: c.traits.filter((t) => t.id !== pair.id) });
          render();
        };
        line.appendChild(dropPair);
        wrap.appendChild(line);
      }
      const addLine = el("div", "trait-line");
      const addTrait = el("button", "btn btn-ghost", t("client.manager.add_trait"));
      addTrait.type = "button";
      addTrait.onclick = () => {
        if (busy) return;
        state = bidDraft.addTraitPair(state!, bid.key, c.key);
        render();
      };
      addLine.appendChild(addTrait);
      wrap.appendChild(addLine);
    }

    if (c.kind === "list") {
      // One price over an explicit list of monkes: at most ONE of them
      // fills per slot.
      const line = el("div", "trait-line");
      const listInput = document.createElement("textarea");
      listInput.rows = 4;
      listInput.placeholder = t("client.manager.list_placeholder");
      listInput.value = c.listText;
      // The listed monkes' art fills in as numbers are typed.
      let strip = spriteStrip(previewMonkes(c.listText));
      listInput.oninput = () => {
        patch({ listText: listInput.value });
        const next = spriteStrip(previewMonkes(listInput.value));
        strip.replaceWith(next);
        strip = next;
        refreshSummary();
      };
      listInput.onblur = () => touch("list");
      controls.list = listInput;
      line.appendChild(field(t("client.manager.list_label"), listInput));
      line.appendChild(strip);
      wrap.appendChild(line);
    }
    return wrap;
  }

  placeBtn.addEventListener("click", () => {
    if (busy || opts.isPageBusy() || !state) return;
    state = bidDraft.addBid(state);
    render();
  });

  /** Offers whose monkes moved since they were signed (or never had a
   * location): their per-item psbts no longer match and need re-signing.
   * Slots whose coins the wallet no longer holds are skipped — a refresh
   * re-spends the slot's exact set, so it could not be funded anyway. */
  const staleOfferIds = (): string[] =>
    serverBids.flatMap((b) =>
      b.slots
        .filter((slot) => !placement.staleSlot(slot, live))
        .flatMap((slot) => slot.components.filter((c) => c.validItems < c.totalItems).map((c) => c.id)),
    );

  /** Re-sign every stale offer (one wallet signature each; each commits on
   * its own, so a rejection keeps what was done), then reload. */
  async function refreshStale(onProgress: (label: string) => void): Promise<{ offers: number; items: number }> {
    const ids = staleOfferIds();
    let offers = 0;
    let items = 0;
    try {
      for (const [i, id] of ids.entries()) {
        items += await flows.refreshCollectionOffer(account!, id, (p) =>
          onProgress(t("client.manager.refreshing", { i: i + 1, n: ids.length, step: p })),
        );
        offers += 1;
        recordChange({ t: "coll-refreshed", id });
      }
    } finally {
      if (offers > 0) await load().catch(() => {});
    }
    return { offers, items };
  }
  const refreshedToast = ({ offers, items }: { offers: number; items: number }) =>
    toast(
      `${t("client.manager.refreshed_offers", { n: offers })} — ${t("client.manager.refreshed_items", { n: items })}`,
      "ok",
    );

  refreshBtn.addEventListener("click", async () => {
    if (busy || opts.isPageBusy()) return;
    setBusy(true);
    try {
      refreshedToast(await refreshStale((label) => {
        refreshBtn.textContent = `${label.toUpperCase()}…`;
      }));
    } catch (err) {
      toast(String((err as Error)?.message ?? err), "error");
    } finally {
      setBusy(false);
      refreshSummary();
    }
  });

  /** Placed components → public rows + journal, one entry per bid. */
  function syncPlaced(applied: flows.BidChangeResult["succeeded"]): void {
    const byBid = new Map<string, PlacedBid>();
    for (const a of applied) {
      if (!a.bidId) continue;
      const draft = state?.bids.find((b) => b.key === a.bidKey);
      const count = draft ? (bidDraft.parseCount(draft.countText) ?? draft.slots.length) : 1;
      let placed = byBid.get(a.bidId);
      if (!placed) {
        placed = {
          id: a.bidId,
          bidder: a.offer.bidder,
          count,
          pricing: draft?.pricing ?? "same",
          components: [],
        };
        byBid.set(a.bidId, placed);
      }
      const existing = placed.components.find((c) => c.componentIndex === a.component.componentIndex);
      if (existing) {
        // The best fill is the highest price across slots.
        if (a.component.priceSats > existing.priceSats) {
          existing.priceSats = a.component.priceSats;
          existing.offerId = a.offer.id;
        }
        existing.left = Math.max(existing.left, a.slotIndex + 1);
      } else {
        placed.components.push({
          componentIndex: a.component.componentIndex,
          offerId: a.offer.id,
          priceSats: a.component.priceSats,
          attributeFilter: a.component.attributeFilter,
          totalItems: a.offer.totalItems,
          left: count,
        });
      }
    }
    for (const placed of byBid.values()) {
      opts.insertMarketRows(placed);
      recordChange({ t: "bid-placed", bid: placed });
    }
  }

  /** Failed creates after a commit: toast them and offer RETRY (signed
   * fundings are still in hand — no new prompts). */
  function reportFailures(result: flows.BidChangeResult): void {
    for (const failure of result.failed) {
      toast(t("client.manager.bid_failed", { error: failure.error.slice(0, 120) }), "error");
    }
    pendingRetry = result.retryFailed ?? null;
    retryBtn.hidden = pendingRetry === null;
    if (pendingRetry) {
      const n = result.failed.length;
      retryBtn.textContent = t("client.manager.retry", { n });
    }
  }

  retryBtn.addEventListener("click", async () => {
    if (busy || opts.isPageBusy() || !pendingRetry) return;
    setBusy(true);
    try {
      const result = await pendingRetry();
      syncPlaced(result.succeeded);
      if (result.succeeded.length > 0) toast(t("client.manager.placed_count", { n: result.succeeded.length }), "ok");
      reportFailures(result);
      await load();
    } catch (err) {
      toast(String((err as Error)?.message ?? err), "error");
    } finally {
      setBusy(false);
    }
  });

  discardBtn.addEventListener("click", () => {
    if (busy || !state) return;
    state = bidDraft.fromServer(serverBids, account.ordinalsAddress);
    touched.clear();
    confirmAttempted = false;
    render();
  });

  confirmBtn.addEventListener("click", async () => {
    if (busy || opts.isPageBusy() || !state) return;
    if (!doc) {
      // Market data still loading: wait for it (the plan follows).
      setBusy(true);
      try {
        await ready;
      } catch (err) {
        toast(t("client.manager.market_data_failed", { error: String((err as Error)?.message ?? err) }), "error");
        return;
      } finally {
        setBusy(false);
      }
    }
    if (!plan) return;
    const ctx = resolveCtx();
    // A blocked attempt marks every incomplete field (red borders + the
    // summary line) and goes no further.
    const errors = bidDraft.draftErrors(state, ctx);
    if (errors.length > 0) {
      confirmAttempted = true;
      refreshSummary();
      return;
    }
    const changes = bidDraft.deriveChangeSet(state, plan, ctx);
    // For the journal / market-table sync: whole bids that die (explicitly,
    // on chain, or by recreation) and components removed from survivors.
    const dyingBidIds = new Set(changes.removeBidIds);
    const removedComponents: { bidId: string; componentIndex: number }[] = [];
    for (const bid of state.bids) {
      if (!bid.serverId) continue;
      const ids = bid.components.flatMap((c) => (c.status === "open" ? [] : c.offerIds)).filter((id): id is string => id !== null);
      if (ids.length > 0 && ids.every((id) => changes.implicitCancelIds.includes(id))) dyingBidIds.add(bid.serverId);
      if (dyingBidIds.has(bid.serverId)) continue;
      for (const c of bid.components) {
        if (c.status === "removed" && c.componentIndex !== null) {
          removedComponents.push({ bidId: bid.serverId, componentIndex: c.componentIndex });
        }
      }
    }
    if (dyingBidIds.size === 0 && removedComponents.length === 0 && changes.creates.length === 0) {
      toast(t("client.manager.nothing_to_confirm"), "info");
      return;
    }
    setBusy(true);
    try {
      const result = await flows.applyBidChanges(account, changes, (label) => {
        progress.textContent = `${label}…`;
      });
      // Nothing irreversible happened (a rejected signature, a failed
      // preflight): the draft stands as it was, nothing to sync.
      const committed =
        result.removed > 0 || result.mgmtTxid !== null || result.succeeded.length > 0 || !!result.retryFailed;
      if (!committed) {
        for (const failure of result.failed) toast(failure.error.slice(0, 160), "error");
        return;
      }
      // Journal + market-table sync per realized change (implicit cancels
      // included — the mgmt broadcast killed them server-side).
      for (const bidId of dyingBidIds) {
        opts.removeMarketBid(bidId);
        recordChange({ t: "bid-cancelled", bidId });
      }
      for (const rc of removedComponents) {
        opts.removeMarketComponent(rc.bidId, rc.componentIndex);
        recordChange({ t: "component-cancelled", ...rc });
      }
      syncPlaced(result.succeeded);
      const parts: string[] = [];
      const reSigned = new Set(changes.reSigned);
      const placed = result.succeeded.filter((a) => !reSigned.has(a.component)).length;
      if (dyingBidIds.size + removedComponents.length > 0) {
        parts.push(t("client.manager.parts_cancelled", { n: dyingBidIds.size + removedComponents.length }));
      }
      if (placed > 0) parts.push(t("client.manager.parts_placed", { n: placed }));
      if (result.succeeded.length > placed) parts.push(t("client.manager.parts_resigned", { n: result.succeeded.length - placed }));
      if (result.mgmtTxid) parts.push(t("client.manager.parts_reorganized"));
      if (parts.length > 0) toast(t("client.manager.bids_updated", { parts: parts.join(", ") }), "ok");
      reportFailures(result);
      await load();
      // Untouched bids whose monkes moved get re-signed in the same sitting.
      const refreshed = await refreshStale((label) => {
        progress.textContent = `${label}…`;
      });
      if (refreshed.offers > 0) refreshedToast(refreshed);
    } catch (err) {
      const message = String((err as Error)?.message ?? err);
      if (message.includes("already reserved") || message.includes("reload your bids")) {
        toast(t("client.manager.changed_elsewhere"), "error");
        await load().catch(() => {});
      } else {
        toast(message, "error");
      }
    } finally {
      setBusy(false);
      setGuard(state !== null && bidDraft.isDirty(state));
    }
  });

  // Instant first paint from the last-seen bids (read-only until the fresh
  // set replaces them — same layout when nothing changed).
  const snapshot = bidStorage.readBidsSnapshot(account.ordinalsAddress);
  if (snapshot) {
    list.classList.add("is-stale");
    applyBids(snapshot);
  }
  void load().catch((err) => {
    if (list) {
      list.classList.remove("is-stale");
      document.documentElement.style.removeProperty("--your-bids-h");
      list.replaceChildren(el("p", "dim", t("client.manager.load_failed")));
    }
    console.error(err);
  });

  return {
    reload: () => load().catch(() => {}),
    confirmInFlight: () => busy,
  };
}
